import { useEffect, useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiIcon,
  MiPlaceholderArea,
  MiSearchInput,
} from "@miplatform/components";
import { ALL } from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { mdiMagnify } from "@mdi/js";
import { SidebarLayout } from "@mibuildweb/components";
import { InputAdornment } from "@mui/material";
import { Box } from "@mui/system";
import {
  BidFilterSidebar,
  initialBidFilters,
} from "./components/BidFilterSidebar";
import { BidList } from "./components/BidList";
import { BidsPageHeader } from "./components/BidsPageHeader";
import { useBidFilters } from "./hooks/useBidFilters";
import { DynamicListFilter } from "../components/DynamicListFilter/DynamicListFilter";
import { BidCountFilter, BidFilter, GetBidsParams } from "./types";
import {
  isCountFilterKey,
  tabToPlanStatusMap,
  useBidsViewModel,
} from "./useBidsViewModel";

export function BidsList() {
  const { query, params, navigate } = useRouter();
  const { classes, theme } = useStyles();

  const {
    bids,
    isLoading: isBidsLoading,
    search,
    debouncedSearch,
    setSearch,
    getBidsFiltered,
    setBids,
    tabCounts,
    bidFilterCounts,
    getDaysUntilDue,
    handleAssignBid,
    handleStartTask,
    bidsRefreshToken,
  } = useBidsViewModel();

  const {
    filters,
    accounts,
    communities,
    seriesGroups,
    series,
    builderOptions,
    communityOptions,
    seriesGroupOptions,
    seriesOptions,
    assigneeOptions,
    planStatusOptions,
    handleFilterApply,
    getFilterData,
    getFiltersFromSearchParams,
    setSearchParams,
    isLoading: isFiltersLoading,
  } = useBidFilters();

  const [countFilters, setCountFilters] = useState<BidCountFilter[]>([]);

  const dataProvider = (params: GetBidsParams) =>
    getBidsFiltered({ ...params, filters, countFilters });

  const handleCountFilterApply = (selectedFilters: BidFilter) => {
    setCountFilters(
      Object.entries(selectedFilters)
        .filter(([key, value]) => isCountFilterKey(key) && value !== ALL)
        .map(([key, value]) => ({ key, value }))
    );
  };

  const refreshKey = JSON.stringify({
    filters,
    countFilters,
    bidsRefreshToken,
  });

  const resetFilters = () => {
    navigate("/estimating/bids/list");
  };

  const handleListSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value },
    } = event;
    setSearch(value);
  };

  useEffect(() => {
    getFilterData();
  }, []);

  useEffect(() => {
    setSearchParams((prevParams) => {
      if (debouncedSearch) {
        prevParams.set("search", debouncedSearch);
      } else {
        prevParams.delete("search");
      }
      return prevParams;
    });
  }, [debouncedSearch]);

  useEffect(() => {
    setCountFilters([]);
    setSearchParams((prevParams) => {
      if (params.tab && params.tab !== "search") {
        const status = tabToPlanStatusMap[params.tab];
        prevParams.set("planStatus", status);
        prevParams.set("groupBy", "bidRequestId");
        prevParams.set("sortBy", "dueDate asc");
      } else {
        prevParams.delete("planStatus");
      }
      return prevParams;
    });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const newFilters = getFiltersFromSearchParams()!;
    handleFilterApply(newFilters);
  }, [params.tab]);

  useEffect(() => {
    if (query.search && typeof query.search === "string") {
      setSearch(query.search);
    }
  }, [query.search]);

  const isSearchAllResults = useMemo(() => {
    return params.tab === "search";
  }, [params.tab]);

  const searchAllBidsContent = () => {
    return (
      <SidebarLayout
        sidebarContent={
          <BidFilterSidebar
            builderOptions={builderOptions}
            communityOptions={communityOptions}
            seriesGroupOptions={seriesGroupOptions}
            seriesOptions={seriesOptions}
            assigneeOptions={assigneeOptions}
            planStatusOptions={planStatusOptions}
            filters={filters}
            onApplyFilters={handleFilterApply}
            resetFilters={resetFilters}
            loading={isFiltersLoading || isBidsLoading}
          />
        }
      >
        <Box className={classes.searchContainerFiltered}>
          <MiPlaceholderArea
            showLogo
            containerStyle={{
              backgroundColor: theme.palette.common.white,
              height: "auto",
            }}
            logoStyle={{ height: 32 }}
          />
          <MiSearchInput
            value={search}
            handleChange={handleListSearch}
            data-testid="bids-search-input"
            className={classes.searchInput}
            textfieldProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MiIcon
                    path={mdiMagnify}
                    size={0.8}
                    color={theme.palette.text.secondary}
                  />
                </InputAdornment>
              ),
            }}
            shrink
          />
        </Box>
        <Box className={classes.bidSideBarListWrapper}>
          <BidList
            data={bids}
            setData={setBids}
            dataProvider={dataProvider}
            filters={filters}
            search={debouncedSearch.trim()}
            accounts={accounts}
            communities={communities}
            seriesGroups={seriesGroups}
            series={series}
            getDaysUntilDue={getDaysUntilDue}
            assigneeOptions={assigneeOptions}
          />
        </Box>
      </SidebarLayout>
    );
  };

  return (
    <>
      <BidsPageHeader
        isLoading={isBidsLoading}
        search={search}
        setSearch={setSearch}
        tabCounts={tabCounts}
      />
      {isSearchAllResults ? (
        searchAllBidsContent()
      ) : (
        <Box className={classes.tabContentWrapper}>
          <DynamicListFilter
            key={params.tab}
            options={bidFilterCounts}
            filters={filters}
            initialFilters={initialBidFilters}
            onApplyFilters={handleCountFilterApply}
            loading={isFiltersLoading}
          />
          <Box className={classes.bidListWrapper}>
            <BidList
              data={bids}
              setData={setBids}
              dataProvider={dataProvider}
              filters={filters}
              refreshKey={refreshKey}
              search={debouncedSearch}
              accounts={accounts}
              communities={communities}
              seriesGroups={seriesGroups}
              series={series}
              assigneeOptions={assigneeOptions}
              getDaysUntilDue={getDaysUntilDue}
              handleAssignBid={handleAssignBid}
              handleStartTask={handleStartTask}
            />
          </Box>
        </Box>
      )}
    </>
  );
}

const useStyles = makeStyles()((theme) => ({
  searchContainerFiltered: {
    flex: 0,
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(2),
    alignItems: "center",
    marginTop: 0,
  },
  searchInput: {
    width: "30%",
  },
  bidSideBarListWrapper: {
    flex: 1,
    overflowY: "auto",
  },
  tabContentWrapper: {
    flex: 1,
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(2),
    margin: theme.spacing(2),
    overflow: "hidden",
  },
  bidListWrapper: {
    flex: 1,
    overflowY: "auto",
    backgroundColor: theme.palette.common.white,
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.grey.lightAccent}`,
  },
}));
